/** Service requests tab of the inbox: status and source filters, a table that becomes cards on a phone, paging. */
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IRequestsApi } from '../../api';
import { ServiceRequestSource, ServiceRequestStatus } from '../../api/types';
import { inputClass, labelClass } from '../../components/formStyles';
import { RequestPager } from '../../components/RequestPager';
import { Toast } from '../../components/Toast';
import {
  SERVICE_FILTERS, SERVICE_SOURCE_FILTERS, SERVICE_SOURCE_LABELS, SERVICE_STATUS_LABELS, requestPlace, requestedWhen, serviceRequestPath,
  showsBundleEntries, statusBadgeClass, whenLabel,
} from './requestsUi';
import { BundleIntakeSection } from './BundleIntakeSection';
import { INBOX_PAGE_SIZE, useInboxList } from './useInboxList';

export function ServiceRequestsTab() {
  const api = useMemo<IRequestsApi>(() => createAPIClient(), []);
  const [status, setStatus] = useState<ServiceRequestStatus | ''>('NEW');
  const [source, setSource] = useState<ServiceRequestSource | ''>('');
  const [page, setPage] = useState(1);
  const fetchPage = useCallback(
    (pageNo: number) => api.listServiceRequests({ ...(status ? { status } : {}), ...(source ? { source } : {}), page: pageNo, limit: INBOX_PAGE_SIZE }),
    [api, status, source],
  );
  const { rows, pagination, loading, error, reload } = useInboxList(fetchPage, page, 'Could not load service requests');

  return (
    <section aria-label="Service requests">
      <div className="mb-4 grid max-w-lg grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="service-status" className={labelClass}>Show</label>
          <select
            id="service-status" className={`${inputClass} min-h-[44px]`} value={status}
            onChange={(e) => { setStatus(e.target.value as ServiceRequestStatus | ''); setPage(1); }}
          >
            {SERVICE_FILTERS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="service-source" className={labelClass}>From</label>
          <select
            id="service-source" className={`${inputClass} min-h-[44px]`} value={source}
            onChange={(e) => { setSource(e.target.value as ServiceRequestSource | ''); setPage(1); }}
          >
            {SERVICE_SOURCE_FILTERS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {/* Bundle requests and renewals waiting for approval (tracker item 85): open items, so only where New or All shows */}
      {showsBundleEntries(status, source) && <BundleIntakeSection />}

      {error && (
        <div>
          <Toast kind="error">{error}</Toast>
          <button type="button" className="mb-3 text-sm font-semibold text-blue-700 hover:underline min-h-[44px]" onClick={() => void reload()}>Try again</button>
        </div>
      )}
      {loading && rows.length === 0 && !error && <p role="status" className="text-gray-600">Loading service requests…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="rounded-lg bg-white p-6 text-center text-gray-600 shadow" data-testid="empty-service">
          {status === 'NEW' ? 'No new service requests.' : 'No service requests match this filter.'}
        </p>
      )}
      {rows.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="responsive-table min-w-full text-sm">
            <caption className="sr-only">Service requests</caption>
            <thead>
              <tr className="border-b text-left text-gray-600">
                <th scope="col" className="px-4 py-3 font-medium">Customer</th>
                <th scope="col" className="px-4 py-3 font-medium">Service</th>
                <th scope="col" className="px-4 py-3 font-medium">Where</th>
                <th scope="col" className="px-4 py-3 font-medium">From</th>
                <th scope="col" className="px-4 py-3 font-medium">Received</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((request) => (
                <tr key={request.id}>
                  <td data-label="Customer" data-primary className="px-4 py-3 break-words">
                    {request.customer ? request.customer.name : <>{request.contact.name} <span className="text-xs font-normal text-amber-700">(no customer yet)</span></>}
                  </td>
                  <td data-label="Service" className="px-4 py-3 break-words">
                    {request.requestedItem?.name ?? 'Not chosen'}
                    {requestedWhen(request) && <div className="text-xs text-gray-500">{requestedWhen(request)}</div>}
                  </td>
                  <td data-label="Where" className="px-4 py-3 break-words">{requestPlace(request)}</td>
                  <td data-label="From" className="px-4 py-3">{SERVICE_SOURCE_LABELS[request.source] ?? request.source}</td>
                  <td data-label="Received" className="px-4 py-3">{whenLabel(request.createdAt)}</td>
                  <td data-label="Status" className="px-4 py-3">
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(request.status)}`}>{SERVICE_STATUS_LABELS[request.status]}</span>
                  </td>
                  <td className="px-4 py-3 md:text-right">
                    <Link to={serviceRequestPath(request.id)} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline" aria-label={`Open request from ${request.customer?.name ?? request.contact.name}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pagination && <RequestPager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} disabled={loading} />}
    </section>
  );
}
